import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Logo } from './Logo'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'

export default function Navbar({ minimal = false, signInTo = '/account/login' }) {
	const [isScrolled, setIsScrolled] = useState(false)
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [showNavbar, setShowNavbar] = useState(true)
	// The install banner sits above the page in normal flow, so this fixed bar
	// has to start below it and ride up as it scrolls away. 0 when there is none.
	const [bannerOffset, setBannerOffset] = useState(0)
	const [isHovered, setIsHovered] = useState(false)
	const lastScrollY = useRef(0)

	useEffect(() => {
		const handleScroll = () => {
			const scrollPosition = window.scrollY
			setIsScrolled(scrollPosition > 10)

			const banner = document.getElementById('app-install-banner')
			setBannerOffset(
				banner ? Math.max(0, banner.getBoundingClientRect().bottom) : 0
			)

			if (scrollPosition > lastScrollY.current) {
				setShowNavbar(false)
			} else {
				setShowNavbar(true)
			}

			lastScrollY.current = scrollPosition
		}

		handleScroll()
		window.addEventListener('scroll', handleScroll)
		window.addEventListener('lf:install-banner', handleScroll)
		return () => {
			window.removeEventListener('scroll', handleScroll)
			window.removeEventListener('lf:install-banner', handleScroll)
		}
	}, [])

	const scrollToTop = e => {
		e.preventDefault()
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		})
	}

	const scrollToSection = (e, sectionId) => {
		e.preventDefault()
		const element = document.getElementById(sectionId)
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' })
		}
	}

	return (
		<>
			{/* Desktop Navbar */}
			<nav
				style={{ top: bannerOffset + 16 }}
				className={`fixed left-1/2 transform -translate-x-1/2 z-50 transition-all duration-500 outline-none focus:outline-none hidden md:block ${showNavbar ? 'translate-y-0' : '-translate-y-[100px]'}`}
			>
				<div
					className={`${
						isScrolled
							? 'bg-white/80 backdrop-blur-md rounded-4xl py-4 px-6 ring-1 ring-gray-200/50 shadow-lg'
							: 'py-3 px-8'
					} transition-all duration-500 ease-in-out`}
				>
					<div
						className={`flex items-center ${isScrolled ? 'space-x-56 lg:space-x-44' : 'space-x-76 lg:space-x-56'} transition-all duration-500 ease-in-out`}
					>
						<Link
							to="/"
							aria-label="Home"
							className="transition-all duration-500 cursor-pointer"
							onClick={minimal ? undefined : scrollToTop}
						>
							<Logo
								color={'text-gray-600 text-xl'}
								height={isScrolled ? '35px' : '45px'}
								width={isScrolled ? '35px' : '45px'}
								className="transition-all duration-500"
							/>
						</Link>

						{/* Navigation Links */}
						<div className={`flex items-center space-x-8 ${minimal ? 'invisible pointer-events-none' : ''}`}>
							<a
								href="#features"
								onClick={e => scrollToSection(e, 'features')}
								className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-lg cursor-pointer"
							>
								Features
							</a>
							<a
								href="#faq"
								onClick={e => scrollToSection(e, 'faq')}
								className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-lg cursor-pointer"
							>
								FAQ
							</a>
							<a
								href="#pricing"
								onClick={e => scrollToSection(e, 'pricing')}
								className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-lg cursor-pointer"
							>
								Pricing
							</a>
						</div>

						<div className="flex items-center space-x-4 flex-nowrap">
							<Link
								to={signInTo}
								className="text-gray-700 hover:text-blue-600 transition-colors font-semibold whitespace-nowrap text-lg"
							>
								Sign In
							</Link>
							<a
								href="https://chromewebstore.google.com/u/1/detail/relay-auto-refresher-post/ihcgicdogclbieclnldfjmlglkolankb?hl=en"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-0 text-white hover:opacity-90 py-2 px-5 text-base rounded-full font-medium transition-all duration-500 transform hover:scale-105 focus:outline-none whitespace-nowrap"
								style={{
									background:
										'linear-gradient(135deg, #2463eb 0%, #1d4ed8 50%, #1e40af 100%)',
								}}
							>
								<svg
									className="w-6 h-6"
									fill="currentColor"
									viewBox="0 0 32 32"
								>
									<path d="M21.25 11.031h-10.063c-0.969 0-1.844 0.313-2.625 0.781-1.344 0.844-2.25 2.344-2.313 4.031l-3.875-6.719c2.063-2.625 5.219-4.344 8.813-4.344 4.406 0 8.25 2.563 10.063 6.25zM1.875 9.75l5 8.688c0.031 0.031 0.031 0.063 0.063 0.094 0.438 0.781 1.125 1.406 1.938 1.844 0.719 0.344 1.469 0.594 2.313 0.594s1.625-0.25 2.344-0.594l-3.906 6.719c-5.438-0.781-9.625-5.438-9.625-11.094 0-2.313 0.688-4.469 1.875-6.25zM13.813 11.813h7.781c0.5 1.281 0.813 2.719 0.813 4.188 0 6.188-5.031 11.188-11.219 11.188-0.25 0-0.469 0-0.719-0.031l5-8.625c0.031-0.031 0.031-0.063 0.063-0.094 0.406-0.75 0.625-1.563 0.625-2.438v-0.156c-0.063-1.688-1-3.188-2.344-4.031zM7.469 16c0-2.094 1.625-3.75 3.719-3.75s3.75 1.656 3.75 3.75-1.656 3.75-3.75 3.75-3.719-1.656-3.719-3.75z" />
								</svg>
								Add to Chrome
							</a>
						</div>
					</div>
				</div>
			</nav>

			{/* Floating Buttons - Show when scrolling down */}
			<div
				style={{ top: bannerOffset + 16 }}
				className={`fixed left-1/2 transform translate-x-[260px] z-50 hidden md:flex items-center space-x-3 transition-all duration-500 ${
					!showNavbar
						? 'translate-y-0 opacity-100'
						: 'translate-y-[-100px] opacity-0'
				}`}
			>
				{/* Add to Chrome Button */}
				<a
					href="https://chromewebstore.google.com/u/1/detail/relay-auto-refresher-post/ihcgicdogclbieclnldfjmlglkolankb?hl=en"
					target="_blank"
					rel="noopener noreferrer"
					className="flex items-center gap-2 text-white hover:opacity-90 py-2 px-5 text-base rounded-full font-medium transition-all duration-300 transform hover:scale-105 focus:outline-none whitespace-nowrap"
					style={{
						background:
							'linear-gradient(135deg, #2463eb 0%, #1d4ed8 50%, #1e40af 100%)',
						boxShadow: '0 0 20px rgba(37, 99, 235, 0.4)',
					}}
				>
					Try for free
				</a>

				{/* Burger Button */}
				<button
					type="button"
					className="inline-flex items-center justify-center rounded-full w-12 h-12 text-white transition-all duration-300 transform hover:scale-110"
					style={{
						backgroundColor: isHovered ? '#1d4ed8' : '#2563eb',
						boxShadow: '0 0 15px rgba(37, 99, 235, 0.4)',
					}}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					onClick={() => setShowNavbar(true)}
				>
					<span className="sr-only">Open menu</span>
					<Bars3Icon className="h-6 w-6" aria-hidden="true" />
				</button>
			</div>

			{/* Mobile Navbar */}
			<nav
				className="md:hidden fixed left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-lg"
				style={{ top: bannerOffset }}
			>
				<div className="px-4 py-3">
					<div className="flex items-center justify-between">
						<Link
							to="/"
							aria-label="Home"
							onClick={minimal ? undefined : scrollToTop}
							className="cursor-pointer"
						>
							<Logo
								color={'text-gray-600 text-xl'}
								height={'35px'}
								width={'35px'}
							/>
						</Link>

						<button
							type="button"
							className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
							onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
						>
							<span className="sr-only">Toggle menu</span>
							{mobileMenuOpen ? (
								<XMarkIcon className="h-6 w-6" aria-hidden="true" />
							) : (
								<Bars3Icon className="h-6 w-6" aria-hidden="true" />
							)}
						</button>
					</div>
				</div>

				{/* Mobile Menu */}
				{mobileMenuOpen && (
					<div className="border-t border-gray-200 bg-white px-4 py-4 space-y-4">
						{!minimal && (<div className="flex flex-col space-y-3 mb-4">
							<a
								href="#features"
								className="text-gray-700 hover:text-blue-600 font-semibold text-lg py-2 text-center cursor-pointer"
								onClick={e => {
									scrollToSection(e, 'features')
									setMobileMenuOpen(false)
								}}
							>
								Features
							</a>
							<a
								href="#faq"
								className="text-gray-700 hover:text-blue-600 font-semibold text-lg py-2 text-center cursor-pointer"
								onClick={e => {
									scrollToSection(e, 'faq')
									setMobileMenuOpen(false)
								}}
							>
								FAQ
							</a>
							<a
								href="#pricing"
								className="text-gray-700 hover:text-blue-600 font-semibold text-lg py-2 text-center cursor-pointer"
								onClick={e => {
									scrollToSection(e, 'pricing')
									setMobileMenuOpen(false)
								}}
							>
								Pricing
							</a>
						</div>)}
						<Link
							to={signInTo}
							className="flex items-center justify-center gap-2 text-white hover:opacity-90 py-2.5 px-5 text-lg rounded-full font-semibold transition-all transform hover:scale-105 focus:outline-none mb-4"
							style={{
								background:
									'linear-gradient(135deg, #2463eb 0%, #1d4ed8 50%, #1e40af 100%)',
							}}
							onClick={() => setMobileMenuOpen(false)}
						>
							Sign In
						</Link>
						<a
							href="https://chromewebstore.google.com/u/1/detail/relay-auto-refresher-post/ihcgicdogclbieclnldfjmlglkolankb?hl=en"
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center justify-center gap-2 text-white hover:opacity-90 py-2.5 px-5 text-base rounded-full font-medium transition-all transform hover:scale-105 focus:outline-none"
							style={{
								background:
									'linear-gradient(135deg, #2463eb 0%, #1d4ed8 50%, #1e40af 100%)',
							}}
							onClick={() => setMobileMenuOpen(false)}
						>
							<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 32 32">
								<path d="M21.25 11.031h-10.063c-0.969 0-1.844 0.313-2.625 0.781-1.344 0.844-2.25 2.344-2.313 4.031l-3.875-6.719c2.063-2.625 5.219-4.344 8.813-4.344 4.406 0 8.25 2.563 10.063 6.25zM1.875 9.75l5 8.688c0.031 0.031 0.031 0.063 0.063 0.094 0.438 0.781 1.125 1.406 1.938 1.844 0.719 0.344 1.469 0.594 2.313 0.594s1.625-0.25 2.344-0.594l-3.906 6.719c-5.438-0.781-9.625-5.438-9.625-11.094 0-2.313 0.688-4.469 1.875-6.25zM13.813 11.813h7.781c0.5 1.281 0.813 2.719 0.813 4.188 0 6.188-5.031 11.188-11.219 11.188-0.25 0-0.469 0-0.719-0.031l5-8.625c0.031-0.031 0.031-0.063 0.063-0.094 0.406-0.75 0.625-1.563 0.625-2.438v-0.156c-0.063-1.688-1-3.188-2.344-4.031zM7.469 16c0-2.094 1.625-3.75 3.719-3.75s3.75 1.656 3.75 3.75-1.656 3.75-3.75 3.75-3.719-1.656-3.719-3.75z" />
							</svg>
							Add to Chrome
						</a>
					</div>
				)}
			</nav>
		</>
	)
}
